import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";

function StrengthRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-800" : "text-gray-500"}`}>
      {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} className="opacity-40" />}
      {label}
    </div>
  );
}

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const checks = {
    length: next.length >= 8,
    hasNumber: /\d/.test(next),
    hasLetter: /[a-zA-Z]/.test(next),
    diffCurrent: next.length > 0 && next !== current,
    match: next.length > 0 && next === confirm,
  };
  const canSubmit = Object.values(checks).every(Boolean) && current.length > 0;

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setShowNext(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      toast.success("Password updated. Keep it safe!");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <KeyRound size={16} /> Change your password
          </DialogTitle>
          <DialogDescription>
            You can change your password any time. Choose something you&apos;ll remember but
            hard for anyone else to guess.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
          <div>
            <Label>Current password</Label>
            <Input required type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1" autoComplete="current-password"
              data-testid="change-password-current" />
          </div>
          <div>
            <Label>New password</Label>
            <div className="relative mt-1">
              <Input required type={showNext ? "text" : "password"} value={next}
                onChange={(e) => setNext(e.target.value)}
                className="pr-9" autoComplete="new-password"
                data-testid="change-password-new" />
              <button type="button" onClick={() => setShowNext((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                data-testid="change-password-toggle-visibility">
                {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input required type={showNext ? "text" : "password"} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1" autoComplete="new-password"
              data-testid="change-password-confirm" />
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50/70 p-3 space-y-1">
            <StrengthRow ok={checks.length}     label="At least 8 characters" />
            <StrengthRow ok={checks.hasLetter}  label="Contains a letter" />
            <StrengthRow ok={checks.hasNumber}  label="Contains a number" />
            <StrengthRow ok={checks.diffCurrent} label="Different from your current password" />
            <StrengthRow ok={checks.match}      label="Confirmation matches" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !canSubmit}
              className="bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="change-password-submit">
              {busy ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
